<template>
  <div class="certificate-work-order">
    <div class="issue-container">
      <div class="issue-wrap">
        <page-header
          :title="pageTitle"
          :sub-title="pageSubTitle"
        >
          <template #rightArea>
            <div class="wo-header-actions">
              <!-- 동영상 가이드(공용 컴포넌트) — COS에서 mp4를 새 탭으로 연다. 문서 가이드 왼쪽에 둔다. -->
              <video-guide-button />
              <!-- 가이드 다운로드(공용 컴포넌트) — 수기 작업지시서 목록 등 타 화면과 공유한다. -->
              <guide-download-button />
              <button
                type="button"
                class="wo-tour-help-btn"
                @click="startTour"
              >
                <i class="fa-solid fa-circle-question"></i>
                {{ $t('workorder_tour_help_btn') }}
              </button>
            </div>
          </template>
        </page-header>
        <progress-wrap-four
          data-tour="wo-progress"
          :step="currentStep + 1"
          :progressTexts="progressTexts"
        />

        <div class="step-content">
        <!-- 입력 스텝(1~3)은 keep-alive로 캐시한다. v-if 전환 시 컴포넌트가 파기되지 않아
             중첩 입력 폼(CustomerInfoForm/WorkerInfoForm)까지 내부 상태가 그대로 유지되므로,
             한 번 진입 후 스텝을 앞뒤로 이동해도 입력값이 사라지지 않는다.
             Step4(확인)는 항상 최신 집계 데이터를 보여야 하므로 캐시에서 제외한다(매번 fresh 렌더).
             각 스텝은 단일 루트라 fallthrough class(step-panel)가 루트에 그대로 적용된다. -->
        <!-- 브랜치 사이 주석은 keep-alive가 별도 child로 인식해(“expects exactly one child”)
             넣지 않는다. Step1~3=캐시(입력 보존), Step4=v-else로 캐시 제외(항상 fresh). -->
        <keep-alive :include="['Step1', 'Step2', 'Step3']">
          <Step1
            v-if="currentStep === 0"
            :key="'s1-' + wizardEpoch"
            ref="step1"
            class="step-panel"
            @select-client="handleClientSelection"
          />
          <Step2
            v-else-if="currentStep === 1"
            :key="'s2-' + wizardEpoch"
            ref="step2"
            class="step-panel"
            :initial-data="certificateInfo"
          />
          <Step3
            v-else-if="currentStep === 2"
            :key="'s3-' + wizardEpoch"
            ref="step3"
            class="step-panel"
            :initial-data="workOrderData"
            :self-issued-map="detectedSelfIssues"
            @self-issue-detected="handleSelfIssueDetected"
          />
          <Step4
            v-else
            ref="step4"
            class="step-panel"
            :client-data="selectedClient"
            :customer-data="certificateInfo"
            :prosthetic-data="workOrderData"
            :prevStep="prevStep"
            :complete="complete"
          />
        </keep-alive>
      </div>

      <div class="step-footer" v-if="currentStep < 3">
        <div class="button-group" data-tour="wo-nav">
          <button
            @click="prevStep"
            :disabled="currentStep === 0"
            class="btn-secondary"
          >
            <i class="fa-solid fa-arrow-left"></i>
            {{ $t('workorder_btn_prev') }}
          </button>

          <button
            v-if="currentStep < steps.length - 1"
            @click="nextStep"
            :disabled="!canProceed"
            class="btn-primary"
          >
            {{ $t('workorder_btn_next') }}
            <i class="fa-solid fa-arrow-right"></i>
          </button>
          <button
            v-else
            @click="complete"
            class="btn-primary"
          >
            <i class="fa-solid fa-paper-plane"></i>
            {{ $t('workorder_btn_submit') }}
          </button>
        </div>
        </div>
      </div>
    </div>

    <!-- 사용법 안내 투어 (antd Tour): 현재 단계에 맞는 스텝만 표시 -->
    <a-tour
      :open="tourOpen"
      v-model:current="tourCurrent"
      :steps="tourSteps"
      @close="onTourClose"
      @finish="onTourClose"
    />

    <!-- 작업지시서 제출 성공 후 실물카드 인쇄 유도 모달 (SPEC-CHINA-CERT-CARD-PRINT-001).
         open은 이 컴포넌트가 전적으로 소유(v-model 아님) — continue/goto-list 두 이벤트로만
         사용자 의도를 구분해 처리한다. -->
    <cert-card-work-order-print-modal
      :open="printPromptModal.open"
      :cards="printPromptModal.cards"
      @continue="onPrintPromptContinue"
      @goto-list="onPrintPromptGotoList"
    />
  </div>
</template>

<script>
import Step1 from './components/Step1.vue';
import Step2 from './components/Step2.vue';
import Step3 from './components/Step3.vue';
import Step4 from './components/Step4.vue';
import PageHeader from './components/PageHeader.vue';
import ProgressWrapFour from './components/ProgressWrapFour.vue';
import GuideDownloadButton from '@/components/GuideDownloadButton.vue';
import VideoGuideButton from '@/components/VideoGuideButton.vue';
import { WorkOrderService } from './services/WorkOrderService';
import { ValidationUtils } from './utils/ValidationUtils';
import { STEPS } from './constants/WorkOrderConstants';
import { businessNumberFormatter, twoBtnDialog } from '@/utils/utils';
import { RouterEnum } from '@/enums/router.enums';
import certificateCardApi from '@/apis/certificateCard';
import { h } from 'vue';
import CertCardWorkOrderPrintModal from '../CertificateCard/components/CertCardWorkOrderPrintModal.vue';

export default {
  name: 'CertificateWorkOrder',
  components: {
    Step1,
    Step2,
    Step3,
    Step4,
    PageHeader,
    ProgressWrapFour,
    GuideDownloadButton,
    VideoGuideButton,
    CertCardWorkOrderPrintModal
  },
  data() {
    return {
      currentStep: 0,
      selectedClient: null,
      certificateInfo: {},
      workOrderData: {},
      workOrderService: null,
      // 환자 자가발급(자가발급 인증서) 역연결 감지 목록 — { [randomNo]: { teeth, products } }.
      // 저장 직전 최종 데이터와 비교(diff)해 수정 여부를 판정하고, 수정된 카드만 회수(recover) 대상으로 삼는다.
      detectedSelfIssues: {},
      tourOpen: false,
      tourCurrent: 0,
      tourSeenKey: 'certificate_work_order_tour_seen_v2',
      // 제출 성공 후 keep-alive 캐시된 Step1~3 인스턴스를 강제 재마운트하기 위한 카운터.
      // resetWizard()에서 증가시키면 :key가 바뀌어 새 인스턴스가 생성되고, 이전 입력값이 남지 않는다.
      wizardEpoch: 0,
      // 제출 성공 후 인쇄 유도 모달 상태 (SPEC-CHINA-CERT-CARD-PRINT-001).
      // 이 컴포넌트가 open을 전적으로 소유(v-model 아님) — onPrintPromptContinue/GotoList가
      // 명시적으로 false로 되돌린다.
      printPromptModal: {
        open: false,
        cards: []
      }
    }
  },
  created() {
    this.workOrderService = new WorkOrderService(this.$store);
    // 진입 시 ?step= 쿼리를 읽어 도달 가능한 단계로 이동 (히스토리 미추가)
    this.applyStepFromQuery(true);
  },
  mounted() {
    // 최초 방문 시 사용법 투어를 1회 자동 노출한다. localStorage로 재노출을 막고,
    // 이후에는 헤더의 '사용법' 버튼으로 다시 볼 수 있다.
    this.$nextTick(() => {
      try {
        if (!localStorage.getItem(this.tourSeenKey)) {
          this.tourOpen = true;
          this.startTourPositionSync();
        }
      } catch (e) {
        // localStorage 접근 불가 시 자동 노출만 생략
      }
    });
  },
  beforeUnmount() {
    // 투어가 열린 채 화면을 떠나면 rAF 루프를 정리한다.
    this.stopTourPositionSync();
  },
  computed: {
    pageTitle() {
      return this.$t('workorder_title');
    },
    pageSubTitle() {
      return `${this.$t('workorder_desc_1')}<br/>${this.$t('workorder_desc_2')}<br/>${this.$t('workorder_desc_3')}`;
    },
    progressTexts() {
      return [
        this.$t('workorder_step1_title'),
        this.$t('workorder_step2_title'),
        this.$t('workorder_step3_title'),
        this.$t('workorder_step4_title')
      ];
    },
    
    steps() {
      return STEPS;
    },
    
    canProceed() {
      switch(this.currentStep) {
        case 0:
          return this.selectedClient !== null;
        case 1:
          // Step2는 내부 검증을 통해 확인
          return true;
        case 2:
          // Step3 검증 로직 추가
          return true;
        default:
          return true;
      }
    },

    // 저장된 단계 데이터 기준으로 도달 가능한 최대 단계(0-based)를 계산한다.
    // dialog 없이 조용히 판정하며, validateFinalData와 동일한 기준을 사용한다.
    maxReachableStep() {
      if (!this.selectedClient) return 0;
      if (!this.certificateInfo || !this.certificateInfo.name) return 1;
      if (!this.workOrderData || !this.workOrderData.selectedTeeth || this.workOrderData.selectedTeeth.length === 0) return 2;
      return 3;
    },

    // 현재 단계(currentStep)에 맞는 antd Tour 스텝 배열을 만든다.
    // 각 단계는 v-if로 현재 화면만 DOM에 있으므로, 투어도 해당 화면의 요소만 가리킨다.
    // target을 함수로 지정해 열릴 때 실시간으로 DOM을 조회한다(없으면 null → 화면 중앙 표시).
    tourSteps() {
      const t = (key) => this.$t(key);
      const q = (selector) => () => document.querySelector(selector);
      // antd Tour는 nextButtonProps.children이 '함수'일 때만 라벨을 적용하고,
      // 문자열이면 무시하고 내장 로케일(Next/Finish)로 폴백한다. 따라서 함수로 감싼다.
      const navBtns = {
        prevButtonProps: { children: () => t('workorder_tour_btn_prev') },
        nextButtonProps: { children: () => t('workorder_tour_btn_next') }
      };
      const finishBtns = {
        prevButtonProps: { children: () => t('workorder_tour_btn_prev') },
        nextButtonProps: { children: () => t('workorder_tour_btn_finish') }
      };

      // @MX:NOTE: SPEC-CHINA-WO-USAGE-GUIDE — Step3/Step4 Tour에 미니앱 참고 이미지(certilife CDN) 첨부.
      //   S3=실물 인증카드(防伪码 위치 안내), S4=환자가 받는 我的认证卡(수신 결과) 화면.
      const cover = (src) => h('img', {
        src,
        style: 'width: 100%; max-height: 380px; object-fit: contain; border-radius: 8px; display: block; background: #f5f6f7;'
      });
      const S3_COVER = 'https://certilife.blob.core.windows.net/certilife/xinlaian/dentalmax/file_1784683275371.jpg';
      const S4_COVER = 'https://certilife.blob.core.windows.net/certilife/xinlaian/dentalmax/file_1784683275370.jpg';

      const progress = {
        title: t('workorder_tour_progress_title'),
        description: t('workorder_tour_progress_desc'),
        target: q('[data-tour="wo-progress"]'),
        ...navBtns
      };
      const nav = {
        title: t('workorder_tour_nav_title'),
        description: t('workorder_tour_nav_desc'),
        target: q('[data-tour="wo-nav"]'),
        ...navBtns
      };

      let mid = [];
      switch (this.currentStep) {
        case 0:
          mid = [
            { title: t('workorder_tour_s1_search_title'), description: t('workorder_tour_s1_search_desc'), target: q('[data-tour="s1-search"]'), ...navBtns },
            { title: t('workorder_tour_s1_list_title'), description: t('workorder_tour_s1_list_desc'), target: q('[data-tour="s1-list"]'), ...navBtns },
            // 거래처를 못 찾았을 때 등록 경로 안내 — target(s1-register)은 검색결과 없음일 때만 존재, 없으면 중앙 표시
            { title: t('workorder_tour_s1_register_title'), description: t('workorder_tour_s1_register_desc'), target: q('[data-tour="s1-register"]'), ...navBtns }
          ];
          break;
        case 1:
          mid = [
            { title: t('workorder_tour_s2_customer_title'), description: t('workorder_tour_s2_customer_desc'), target: q('[data-tour="s2-customer"]'), ...navBtns },
            { title: t('workorder_tour_s2_worker_title'), description: t('workorder_tour_s2_worker_desc'), target: q('[data-tour="s2-worker"]'), ...navBtns }
          ];
          break;
        case 2:
          mid = [
            { title: t('workorder_tour_s3_input_title'), description: t('workorder_tour_s3_input_desc'), cover: cover(S3_COVER), target: q('[data-tour="s3-input"]'), ...navBtns },
            { title: t('workorder_tour_s3_add_title'), description: t('workorder_tour_s3_add_desc'), target: q('[data-tour="s3-add"]'), ...navBtns },
            { title: t('workorder_tour_s3_cards_title'), description: t('workorder_tour_s3_cards_desc'), target: q('[data-tour="s3-cards"]'), ...navBtns }
          ];
          break;
        case 3:
          mid = [
            { title: t('workorder_tour_s4_review_title'), description: t('workorder_tour_s4_review_desc'), target: q('[data-tour="s4-review"]'), ...navBtns },
            { title: t('workorder_tour_s4_submit_title'), description: t('workorder_tour_s4_submit_desc'), cover: cover(S4_COVER), target: q('[data-tour="s4-submit"]'), ...navBtns }
          ];
          break;
      }

      // step4는 하단 이동 버튼(wo-nav)이 없으므로 nav 안내 스텝을 제외한다.
      const steps = this.currentStep === 3 ? [progress, ...mid] : [progress, ...mid, nav];

      // 마지막 스텝의 다음 버튼을 '완료'로 표시한다.
      if (steps.length > 0) {
        steps[steps.length - 1] = { ...steps[steps.length - 1], ...finishBtns };
      }
      return steps;
    }
  },
  watch: {
    // 브라우저 뒤로/앞으로가기, URL 직접 수정으로 ?step= 이 바뀌면 도달 가능 범위로 클램프해 반영
    '$route.query.step'(value) {
      const target = this.clampStep(this.parseStepQuery(value));
      if (target !== this.currentStep) {
        this.currentStep = target;
      } else {
        // 요청 단계가 도달 범위를 초과해 클램프된 경우 URL을 실제 단계로 교정
        this.writeStepToQuery(target, true);
      }
    },
    // 단계가 바뀌면 ?step= 을 동기화한다 (히스토리에 남겨 뒤로가기 이동 지원)
    currentStep(value) {
      this.writeStepToQuery(value, false);
    }
  },
  methods: {
    // ?step= 쿼리(1~4)를 파싱한다. 값이 없거나 잘못되면 1단계로 처리한다.
    parseStepQuery(raw) {
      const n = parseInt(Array.isArray(raw) ? raw[0] : raw, 10);
      if (Number.isNaN(n)) return 1;
      return Math.min(Math.max(n, 1), 4);
    },
    // 요청 단계(1-based)를 도달 가능 범위로 클램프해 0-based 인덱스로 반환한다.
    clampStep(oneBasedStep) {
      return Math.min(oneBasedStep - 1, this.maxReachableStep);
    },
    // 현재 단계(0-based)를 URL ?step= 에 기록한다. useReplace=true면 히스토리에 남기지 않는다.
    //
    // [HARD][버그 재현·수정] 이 화면(위저드)을 벗어난 뒤에는 절대 step 쿼리를 다시 쓰지 않는다.
    // 재현된 사고: 인쇄 유도 모달 '목록으로 가기' → resetWizard()가 currentStep=0으로 되돌리면
    // currentStep watcher가 이 함수를 호출해 this.$router.push({query})를 실행한다. 이때 path를
    // 지정하지 않는 { query } push는 "현재 라우트" 기준으로 해석되므로, 목록 이동
    // this.$router.push('/lab-orders/manual')이 아직 완료(비동기 컴포넌트 로드 등)되기 전이면
    // this.$route는 여전히 이 위저드 라우트다. vue-router는 나중에 시작된 내비게이션을 최신으로
    // 채택하고 먼저 시작된(진행 중이던) 내비게이션을 취소하므로, 컴포넌트 재사용으로 더 빨리
    // 끝나는 이 쿼리 전용 push가 목록 이동을 취소시켜버리고 결국 '/certificate-work-order?step=1'
    // 로 되돌아간다(실사용자 리포트와 100% 일치). 아래 가드는 타이밍에 관계없이 이 화면을 벗어난
    // 뒤의 모든 step 쿼리 재기록을 원천 차단한다 — index.vue의 onPrintPromptGotoList()가
    // '목록 이동 완료 후 resetWizard() 호출'로 순서를 재조정한 것과 이중 방어를 이룬다.
    writeStepToQuery(stepIndex, useReplace) {
      if (this.$route.path !== RouterEnum.CertificateWorkOrder.path) return;
      const stepValue = String(stepIndex + 1);
      if (this.$route.query.step === stepValue) return;
      const query = { ...this.$route.query, step: stepValue };
      const navigation = useReplace
        ? this.$router.replace({ query })
        : this.$router.push({ query });
      if (navigation && typeof navigation.catch === 'function') {
        navigation.catch(() => {});
      }
    },
    // 진입 시 ?step= 을 읽어 도달 가능한 단계로 이동시키고 URL을 교정한다.
    applyStepFromQuery(useReplace) {
      const target = this.clampStep(this.parseStepQuery(this.$route.query.step));
      this.currentStep = target;
      this.writeStepToQuery(target, useReplace);
    },
    formatBusinessNumber(value) {
      return businessNumberFormatter(value);
    },
    handleClientSelection(client) {
      this.selectedClient = client;
      console.log('선택된 거래처:', client);
    },
    async nextStep() {
      const validation = await this.validateCurrentStep();
      
      if (!validation.isValid) {
        this.$message.warning(validation.message);
        return;
      }

      await this.saveCurrentStepData();

      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
      }
    },

    async validateCurrentStep() {
      switch(this.currentStep) {
        case 0:
          return ValidationUtils.validateStep1(this.selectedClient);
        case 1:
          return await ValidationUtils.validateStep2(this.$refs.step2);
        case 2:
          return await ValidationUtils.validateStep3(this.$refs.step3);
        default:
          return { isValid: true };
      }
    },

    async saveCurrentStepData() {
      console.log('[saveCurrentStepData] currentStep:', this.currentStep);
      switch(this.currentStep) {
        case 1:
          this.certificateInfo = this.$refs.step2?.getFormData();
          console.log('[saveCurrentStepData] certificateInfo 저장:', this.certificateInfo);
          break;
        case 2:
          this.workOrderData = this.$refs.step3?.getFormData();
          console.log('[saveCurrentStepData] workOrderData 저장:', this.workOrderData);
          break;
      }
    },
    async prevStep() {
      if (this.currentStep > 0) {
        // 뒤로 이동 전에도 현재 스텝 입력을 부모 집계 상태에 저장한다.
        // keep-alive가 컴포넌트 상태(화면 입력)를 보존하지만, Step4 확인·제출·도달가능단계
        // 계산은 부모의 certificateInfo/workOrderData를 참조하므로 앞으로 이동과 동일하게 동기화한다.
        await this.saveCurrentStepData();
        this.currentStep--;
      }
    },
    async goToStep(index) {
      // 완료된 스텝으로만 이동 가능
      if (index <= this.currentStep && index !== this.currentStep) {
        // 이동 전 현재 스텝 입력을 저장(앞/뒤 방향 무관하게 부모 집계 동기화)
        await this.saveCurrentStepData();
        this.currentStep = index;
      }
    },
    // 작업지시서 제출 성공 후 위저드를 최초 진입 상태로 완전히 되돌린다.
    // data()의 초기값을 그대로 다시 대입해 부모 집계 상태(selectedClient/certificateInfo/workOrderData)를
    // 비우고, wizardEpoch를 증가시켜 keep-alive에 캐시된 Step1~3 인스턴스를 강제로 재마운트한다.
    // (재마운트 없이는 캐시된 컴포넌트 내부 입력값이 남아 다음 작업지시서 작성 시 이전 값이 보인다.)
    resetWizard() {
      const initial = this.$options.data.call(this);
      this.selectedClient = initial.selectedClient;
      this.certificateInfo = initial.certificateInfo;
      this.workOrderData = initial.workOrderData;
      this.detectedSelfIssues = initial.detectedSelfIssues;
      this.wizardEpoch += 1;
      this.currentStep = 0;
    },

    // 인쇄 유도 모달 "계속 만들기"(및 X/마스크/Esc로 닫기) — 이동 없이 위저드만 초기화한다.
    onPrintPromptContinue() {
      this.printPromptModal.open = false;
      this.resetWizard();
    },

    // 인쇄 유도 모달 "목록으로 가기" — 반드시 라우트 전환을 먼저 완료(then)한 뒤에 resetWizard()를
    // 호출한다. resetWizard()가 먼저 실행되면 currentStep=0 변경이 유발하는 동일 라우트 쿼리 push가
    // 아직 끝나지 않은 목록 이동 push와 경합해 목록 이동이 취소되는 사고가 재현된다
    // (원인 상세는 writeStepToQuery() 주석 참조). push 실패(NavigationDuplicated 등)도
    // resetWizard()는 항상 실행되어야 하므로 .catch()로 흡수한다.
    onPrintPromptGotoList() {
      this.printPromptModal.open = false;
      this.$router
        .push(`${RouterEnum.LabOrders.path}/${RouterEnum.LabOrders.Manual.path}`)
        .catch(() => {})
        .then(() => {
          this.resetWizard();
        });
    },

    // Step3(ZirconiaToothInfoForm)이 감지한 환자 자가발급 역연결을 수신한다.
    // 1) 저장 시 diff 판정을 위해 baseline(치아/제품)을 randomNo 기준으로 보관.
    // 2) Step2 환자정보(이름/전화 등)를 자가발급 신원으로 자동채움(편집 가능 — 값을 잠그지 않음).
    //    BE가 내려준 필드만 반영하고, 응답에 없는 필드는 기존 certificateInfo 값을 그대로 둔다.
    handleSelfIssueDetected(payload) {
      const { randomNo, patient, baseline } = payload || {};
      if (!randomNo) return;

      this.detectedSelfIssues = {
        ...this.detectedSelfIssues,
        [randomNo]: baseline,
      };

      if (patient) {
        const mobileDigits = (patient.mobile || '').replace(/\D/g, '');
        this.certificateInfo = {
          ...this.certificateInfo,
          name: patient.name || this.certificateInfo.name,
          mobile: mobileDigits || this.certificateInfo.mobile,
          displayMobile: patient.mobile || this.certificateInfo.displayMobile,
          gender: patient.gender || this.certificateInfo.gender,
          birthDate: patient.birthDate || this.certificateInfo.birthDate,
          surgeryDate: patient.surgeryDate || this.certificateInfo.surgeryDate,
        };
      }
    },

    // 감지된 자가발급 카드(randomNo)의 최종 제출값이 baseline과 달라졌는지 판정한다.
    // 최종 제출(workOrderData.toothDetails)에서 해당 randomNo가 아예 사라졌다면(카드 제거)
    // 이 랜덤넘버는 이번 작업지시서와 무관해진 것으로 보고 회수 대상에서 제외한다(안전한 기본값 —
    // 작업지시서에 포함되지 않은 카드까지 임의로 회수하지 않는다).
    isSelfIssueModified(randomNo, baseline) {
      if (!baseline) return false;

      const details = this.workOrderData?.toothDetails || [];
      const detail = details.find((d) =>
        (d.products || []).some((p) => p.randomNo === randomNo)
      );
      if (!detail) return false;

      const currentTeeth = [...(detail.toothNumbers || [])].sort((a, b) => a - b);
      const baselineTeeth = [...(baseline.teeth || [])].sort((a, b) => a - b);
      if (
        currentTeeth.length !== baselineTeeth.length ||
        currentTeeth.some((v, i) => v !== baselineTeeth[i])
      ) {
        return true;
      }

      const currentProduct = (detail.products || []).find(
        (p) => p.randomNo === randomNo
      );
      const baselineProducts = baseline.products || [];
      const baselineProduct =
        baselineProducts.find((p) => p.randomNo === randomNo) ||
        baselineProducts[0];

      if (!currentProduct || !baselineProduct) return true;

      return (
        (currentProduct.itemName || '') !== (baselineProduct.itemName || '') ||
        (currentProduct.lotNumber || '') !== (baselineProduct.lotNumber || '') ||
        (currentProduct.productCode || '') !== (baselineProduct.productCode || '')
      );
    },

    // 저장 직전 — 감지된 자가발급 카드 중 수정된 것이 있으면 확인모달 후 recover-certificate를 먼저
    // 실행한다(작업지시서가 권위를 갖도록). 회수 실패 시 저장을 중단한다(불일치 상태 방지).
    // 반환값: true면 저장 계속 진행, false면 저장 중단.
    async confirmAndRecoverModifiedSelfIssues() {
      const modifiedRandomNos = Object.keys(this.detectedSelfIssues).filter(
        (randomNo) =>
          this.isSelfIssueModified(randomNo, this.detectedSelfIssues[randomNo])
      );

      if (modifiedRandomNos.length === 0) return true;

      const proceed = await twoBtnDialog(
        this.$t('workorder_self_issue_recover_confirm_title'),
        this.$t('workorder_self_issue_recover_confirm_msg'),
        this.$t('workorder_btn_confirm'),
        this.$t('workorder_btn_cancel')
      );
      if (!proceed) return false;

      for (const randomNo of modifiedRandomNos) {
        try {
          await certificateCardApi.recoverCertificate(randomNo);
        } catch (error) {
          console.error('[자가발급 인증서 회수 실패]', randomNo, error);
          this.$message.error(
            this.$t('workorder_self_issue_recover_failed', { randomNo })
          );
          return false;
        }
      }

      return true;
    },

    async complete() {
      console.log('complete 메소드 호출');
      
      // 최종 데이터 검증
      const finalValidation = ValidationUtils.validateFinalData(
        this.selectedClient, 
        this.certificateInfo, 
        this.workOrderData
      );
      
      if (!finalValidation.isValid) {
        console.error('최종 검증 실패:', finalValidation.errors);
        this.$message.error(finalValidation.errors.join(', '));
        return;
      }

      // 환자 자가발급 역연결 — pre-fill된 카드가 수정되었으면 확인 후 기존 자가발급 인증서를
      // 먼저 회수(recover)한다. 사용자가 취소하거나 회수가 실패하면 저장을 중단한다.
      const canProceedAfterSelfIssueCheck =
        await this.confirmAndRecoverModifiedSelfIssues();
      if (!canProceedAfterSelfIssueCheck) return;

      // 전달할 데이터 준비
      const workOrderData = this.workOrderService.prepareWorkOrderData(
        this.selectedClient,
        this.certificateInfo,
        this.workOrderData
      );

      if (!workOrderData) {
        this.$message.error(this.$t('workorder_err_required'));
        return;
      }

      // SPEC-CHINA-CERT-CARD-PRINT-001: 이번 제출에 실물카드(randomNo)가 배정된 제품이 있으면
      // 제출 성공 후 인쇄를 유도한다. 필드 경로는 prepareWorkOrderData()가 만든
      // workOrderData.prosthetic.toothDetails[].{randomNo, gtinCode, toothNumbers} — 이 값이
      // 그대로 BE createWorkOrder 요청 바디로 전송된다. 같은 randomNo가 toothDetail 엔트리
      // 여러 개(치아 여러 개에 같은 카드)에 걸쳐 나올 수 있어 dedup한다.
      const submittedToothDetails = workOrderData.prosthetic?.toothDetails || [];
      const seenRandomNos = new Set();
      const linkedCards = [];
      submittedToothDetails.forEach((detail) => {
        const randomNo = (detail.randomNo || '').trim();
        if (!randomNo || seenRandomNos.has(randomNo)) return;
        seenRandomNos.add(randomNo);
        linkedCards.push({
          randomNo,
          gtin: detail.gtinCode || '',
          teeth: Array.isArray(detail.toothNumbers) ? [...detail.toothNumbers] : []
        });
      });

      // API 호출
      await this.workOrderService.createWorkOrder(
        workOrderData,
        (response) => {
          console.log('API 성공 응답:', response);
          this.$message.success(this.$t('workorder_success_msg'));
          if (linkedCards.length === 0) {
            // 실물카드가 없는 제출 — 기존 동작 그대로(회귀 없음): 즉시 초기화+이동
            this.resetWizard();
            this.$router.push(`${RouterEnum.LabOrders.path}/${RouterEnum.LabOrders.Manual.path}`);
            return;
          }
          // 실물카드가 있으면 인쇄 유도 모달을 먼저 보여준다 — 초기화+이동은 사용자가
          // "계속 만들기"/"목록으로 가기" 중 하나를 명시적으로 고른 시점에 정확히 1회 수행된다
          // (onPrintPromptContinue/onPrintPromptGotoList, 이 모달 하나가 열려 있는 동안 그
          // 둘 중 하나만 호출될 수 있으므로 중복 실행 여지가 없다).
          this.printPromptModal.cards = linkedCards;
          this.printPromptModal.open = true;
        },
        (error) => {
          console.error('전달 실패:', error);
          // SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 REQ-FE-STEP3-007 — 배정 충돌(TOCTOU) 시
          // BE가 conflicts:[{position, randomNo}]를 반환하면 재선택 안내 (성공분은 BE가 보정 해제).
          const conflicts = error?.response?.data?.conflicts;
          if (Array.isArray(conflicts) && conflicts.length > 0) {
            const positions = conflicts
              .map((c) => c.position)
              .filter(Boolean)
              .join(', ');
            this.$message.error(
              this.$t('workorder_err_card_conflict', { positions })
            );
            return;
          }
          this.$message.error(this.$t('workorder_err_submit'));
        }
      );
    },

    // 사용법 투어를 현재 단계 기준 처음부터 시작한다.
    startTour() {
      this.tourCurrent = 0;
      this.tourOpen = true;
      this.startTourPositionSync();
    },
    // 투어를 닫거나 완료하면 open 상태를 내리고 최초 노출 완료를 저장한다.
    onTourClose() {
      this.tourOpen = false;
      this.stopTourPositionSync();
      try {
        localStorage.setItem(this.tourSeenKey, '1');
      } catch (e) {
        // localStorage 접근 불가 환경은 무시
      }
    },

    // --- 투어 스포트라이트 위치 동기화 ---
    // antd Tour(vc-tour useTarget)는 '열림 / 스텝 변경 / window resize'에만 타깃 위치를 재측정한다.
    // 이 위저드는 콘텐츠가 세로 중앙정렬(justify-content:center)이라, 거래처 목록 등 비동기 콘텐츠가
    // 로드되면 전체가 재중앙정렬되어 밀린다(수백 ms~수 초까지 이어짐). 단발 타이머로는 그 정착을 놓쳐
    // 스포트라이트가 어긋난다. 그래서 투어가 열려 있는 동안 현재 스텝 타깃의 위치를 지속 감시하고,
    // 위치가 바뀐 경우에만 resize를 발생시켜 재측정을 강제한다(변경 시에만 → 부하 최소화).
    fireTourResize() {
      try {
        window.dispatchEvent(new Event('resize'));
      } catch (e) {
        // 구형/비표준 환경 방어
      }
    },
    startTourPositionSync() {
      this.stopTourPositionSync();
      if (typeof requestAnimationFrame !== 'function') {
        this.$nextTick(() => this.fireTourResize());
        return;
      }
      let lastKey = '';
      let frame = 0;
      const loop = () => {
        if (!this.tourOpen) {
          this._tourSyncRaf = null;
          return;
        }
        // 약 6프레임(~100ms)마다 현재 스텝 타깃의 rect를 확인, 바뀐 경우에만 재측정
        if ((frame++ % 6) === 0) {
          const steps = this.tourSteps;
          const step = Array.isArray(steps) ? steps[this.tourCurrent] : null;
          const el = step && typeof step.target === 'function' ? step.target() : null;
          if (el) {
            const r = el.getBoundingClientRect();
            const key = Math.round(r.left) + ',' + Math.round(r.top) + ',' + Math.round(r.width) + ',' + Math.round(r.height);
            if (key !== lastKey) {
              lastKey = key;
              this.fireTourResize();
            }
          }
        }
        this._tourSyncRaf = requestAnimationFrame(loop);
      };
      this._tourSyncRaf = requestAnimationFrame(loop);
    },
    stopTourPositionSync() {
      if (this._tourSyncRaf) {
        cancelAnimationFrame(this._tourSyncRaf);
        this._tourSyncRaf = null;
      }
    }
  }
}
</script>

<style scoped lang="scss">
$Gray-10: #F1F2F3;
$Gray-20: #E8EBED;
$Gray-30: #DDE0E4;
$Gray-40: #C7CCD1;
$Gray-50: #ACB2B9;
$Gray-60: #8D939A;
$Gray-70: #6e747c;
$Gray-80: #4B5158;
$Gray-90: #2F3337;
$Gray-100 : #17191C;
$Yellow: #ffe414;
$Blue : #278AEC;
$SkyBlue : #E1F0FF;

.certificate-work-order {
  max-width: 100%;
  display: flex;
  align-items: start;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
  padding: 0;
  background-color: $Gray-10;
  min-height: 100vh;

  .issue-container {
    width: 100%;
    max-width: 1000px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    flex-direction: column;

    .issue-wrap {
      width: 100%;
      max-width: 1000px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 24px;
      padding: 24px 24px 48px 24px;
      flex-direction: column;
    }
  }
}


.step-content {
  width: 100%;
  max-width: 1000px;

  .step-panel {
    animation: fadeIn 0.3s ease-in-out;
    width: 100%;
  }
}

.step-footer {
  width: 100%;
  max-width: 1000px;
  padding: 24px 0;
  display: flex;
  justify-content: center;
  align-items: center;

  .button-group {
    display: flex;
    gap: 12px;
  }

  button {
    padding: 12px 32px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    transition: all 0.3s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 120px;
    justify-content: center;

    i {
      font-size: 16px;
    }

    &.btn-secondary {
      background: white;
      color: $Gray-70;
      border: 2px solid $Gray-30;

      &:hover:not(:disabled) {
        background: $Gray-10;
        border-color: $Gray-50;
        color: $Gray-90;
      }
    }

    &.btn-primary {
      background: $Yellow;
      color: $Gray-90;
      border: none;

      &:hover:not(:disabled) {
        background: #ffd700;
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(255, 228, 20, 0.3);
      }
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      transform: none !important;
    }
  }
}

.wo-header-actions {
  display: inline-flex;
  align-items: center;
  // page-header-v2는 align-items:stretch(기본) flex라, 이 액션 박스가 제목+설명 높이만큼
  // 늘어난다. 그러면 내부 두 버튼이 서로 다른 수직정렬(사용법=flex-start / 다운로드=center)로
  // 계단식 어긋남이 생긴다. 액션 박스를 상단 정렬해 콘텐츠 높이만 갖게 하면 두 버튼이 한 줄로 정렬된다.
  align-self: flex-start;
  gap: 8px;
}

.wo-tour-help-btn {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid $Gray-30;
  border-radius: 8px;
  background: #fff;
  color: $Gray-80;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  i {
    font-size: 14px;
    color: $Gray-60;
  }

  &:hover {
    background: $Gray-10;
    border-color: $Gray-50;
    color: $Gray-90;

    i {
      color: $Gray-80;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 반응형 디자인
@media (max-width: 768px) {
  .certificate-work-order {
    .issue-container {
      .issue-wrap {
        padding: 16px 16px 32px 16px;
        gap: 16px;
      }
    }
  }

  .step-footer {
    padding: 16px 0;

    .button-group {
      flex-direction: column;
      width: 100%;
      gap: 8px;
    }

    button {
      width: 100%;
      padding: 14px 24px;
      font-size: 16px;
    }
  }
}
</style>

<style lang="scss" scoped>
@import './components/css/issue-certificate.scss';
@import './components/css/implant-abutment.scss';
</style>